import { useMutation, useQuery } from "@tanstack/react-query";
import { checkPendingPayments, processPayment } from "../requests";
import { useAtomValue } from "jotai";
import { userProfile } from "../state";
import { App, Modal } from "antd";
import PaymentWarning from "../components/modals/paymentWarning/paymentWarning";
import { createContext, ReactNode, useContext, useState } from "react";
import PaymentOptions from "../components/modals/paymentOptions/paymentOptions";

type PaymentContextType = {
  makePayment: (
    payload: ProcessPaymentPayload,
    successCallBack?: (data: ProcessPaymentResponse) => void,
  ) => void;
  isPending: boolean;
};

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const PaymentProvider = ({ children }: { children: ReactNode }) => {
  const user = useAtomValue(userProfile);
  const { notification } = App.useApp();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showMethod, setShowMethod] = useState(false);
  const [payload, setPayload] = useState<ProcessPaymentPayload>(
    {} as ProcessPaymentPayload,
  );
  const [onSuccess, setOnSuccess] = useState<
    ((data: ProcessPaymentResponse) => void) | null
  >(null);
  const [channel, setChannel] = useState<PaymentChannel>("Cyberpay");

  const pendingPayment = useQuery({
    queryKey: ["check-pending-payment"],
    queryFn: () => checkPendingPayments(user?.MembershipNo),
    enabled: !!user?.MembershipNo,
  });

  const processPaymentMutation = useMutation({
    mutationKey: ["process-payment"],
    mutationFn: ({
      payload,
      channel,
    }: {
      payload: ProcessPaymentPayload;
      channel?: string;
    }) => processPayment(payload, channel),
  });

  const hasPendingPayment = pendingPayment?.data as string;

  const makePayment = (
    payload: ProcessPaymentPayload,
    // paymentChannel: string = "CyberPay",
    successCallBack?: (data: ProcessPaymentResponse) => void,
  ) => {
    setPayload(payload);
    successCallBack && setOnSuccess(() => successCallBack);
    //setSelectedMethod(paymentChannel);

    if (hasPendingPayment) {
      setShowConfirm(true);
    } else setShowMethod(true);
  };
  const handleConfirm = () => {
    setShowConfirm(false);
    setShowMethod(true);
  };
  const handleSelectMethod = (_channel: string, url: string) => {
    setShowMethod(false);
    initiatePayment(url);
  };

  const initiatePayment = async (url: string) => {
    try {
      await processPaymentMutation.mutateAsync(
        { payload, channel: channel },
        {
          onSuccess: (data) => {
            if (channel?.toLowerCase() === "interswitch") {
              const response = JSON.parse(
                data?.RequestPayLoad,
              ) as InterswitchRequestPayload;
              redirectWeb(response?.Data, url);
            } else {
              onSuccess && onSuccess(data);
            }
          },
        },
      );
    } catch (error: any) {
      notification.error({
        message:
          error?.Message ||
          error?.response?.data?.Message ||
          error?.response?.data?.message ||
          error?.response?.data?.title ||
          error?.message,
      });
    }
  };

  const redirectWeb = (data: InterswitchData, url: string) => {
    const form = document.createElement("form");
    form.method = "POST";
    //form.action = "https://newwebpay.qa.interswitchng.com/collections/w/pay"; // Test URL
    form.action =
      url || "https://newwebpay.interswitchng.com/collections/w/pay";
    form.style.display = "none";

    const fields: InterswitchData = {
      merchant_code: data?.merchant_code,
      pay_item_id: data?.pay_item_id,
      site_redirect_url: data?.site_redirect_url,
      txn_ref: data?.txn_ref,
      amount: data?.amount,
      currency: data?.currency,
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const isPending = processPaymentMutation.isPending;

  return (
    <PaymentContext.Provider value={{ makePayment, isPending }}>
      {children}
      <Modal
        centered
        open={showConfirm}
        onCancel={() => setShowConfirm(false)}
        footer={null}
      >
        <PaymentWarning
          onOk={handleConfirm}
          onCancel={() => setShowConfirm(false)}
        />
      </Modal>
      <Modal
        centered
        open={showMethod}
        onCancel={() => setShowMethod(false)}
        footer={null}
        styles={{ content: { padding: 0 }, body: { paddingInline: "3.75rem" } }}
      >
        <PaymentOptions
          channel={channel}
          setChannel={setChannel}
          onOk={handleSelectMethod}
          onCancel={() => setShowMethod(false)}
        />
      </Modal>
    </PaymentContext.Provider>
  );
};

const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error("usePayment must be used within a PaymentProvider");
  }
  return context;
};

export default usePayment;
